import QueryString from 'query-string'
import omit from 'lodash/omit'
import type { SortTuple } from './Table'
import { Log, LogLevel } from './Log'

const configData = window.CS_CONFIG

const buildApiPutError = (responseBody: string): Error => {
    const fallbackMessage = 'There was an issue processing your request'
    if (!responseBody || responseBody.trim().length === 0) {
        return Error(fallbackMessage)
    }

    try {
        const parsed = JSON.parse(responseBody) as {
            message?: unknown;
            disallowedSpecialtyCategories?: unknown;
        }
        const message = typeof parsed.message === 'string' && parsed.message.trim().length > 0
            ? parsed.message.trim()
            : fallbackMessage
        const disallowedCategories = Array.isArray(parsed.disallowedSpecialtyCategories)
            ? parsed.disallowedSpecialtyCategories
                .filter((category): category is string => typeof category === 'string' && category.trim().length > 0)
                .map(category => category.trim())
            : []

        return disallowedCategories.length > 0
            ? Error(`${message} Disallowed specialty categories: ${disallowedCategories.join(', ')}`)
            : Error(message)
    } catch {
        return Error(fallbackMessage)
    }
}

const makeSortQueryString = (sort: SortTuple[]): string =>
    sort.reduce((acc, [key, value]) => value !== undefined ? acc + `&sort[0][${key}]=${value}` : acc, '').substr(1)

export const ApiFetch = <T extends unknown>(apiRoute: string, queryStringObject?: Record<string, unknown>, isRaw?: boolean): Promise<T> => {
    const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }

    const queryString = queryStringObject ? '?' + QueryString.stringify(omit(queryStringObject, 'sort')) : null
    const sort = queryStringObject?.sort ? `${queryString ? '&' : '?'}${makeSortQueryString(queryStringObject.sort as SortTuple[])}` : null
    const q = `${queryString || ''}${sort || ''}`

    return fetch(configData.ApiBaseUrl + '/' + apiRoute + (q ? q : ''), headers)
        .then(res => {
            if (!res.ok) {
                res.clone().text().then(t => {
                    Log(LogLevel.Error, `GET request ${configData.ApiBaseUrl + '/' + apiRoute + (q ? q : '')} failed with message ${t}`)
                })
                // eslint-disable-next-line no-console
                .catch(console.log)
                
                throw res
            }

            if (isRaw) return res.text()
            else return res.json()
        }) as Promise<T>
}

export const ApiPost = <T extends unknown>(apiRoute: string, queryStringObject?: Record<string, unknown>, object?: Record<string, unknown>): Promise<T> => {
    const queryString = queryStringObject ? '?' + QueryString.stringify(queryStringObject) : null

    const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }

    const init = {
        headers: headers,
        method: 'POST',
        body: JSON.stringify(object)
    }

    return fetch(configData.ApiBaseUrl + '/' + apiRoute + (queryString ? queryString : ''), init)
        .then(res => {
            if (res.status !== 200) {
                if (apiRoute.toLowerCase() !== 'log') {
                    res.text().then(t => {
                        Log(LogLevel.Error, `POST request ${configData.ApiBaseUrl + '/' + apiRoute + (queryString ? queryString : '')} failed with message ${t}`)
                    })
                    // eslint-disable-next-line no-console
                    .catch(console.log)
                }
                throw Error('There was an issue processing your request')
            }
            if (res.ok) {
                return res.text().then(t => {
                    return Promise.resolve(t.length > 0 ? JSON.parse(t) as T : true)
                })
            }
            if (apiRoute.toLowerCase() !== 'log') {
                Log(LogLevel.Error, `POST request ${configData.ApiBaseUrl + '/' + apiRoute + (queryString ? queryString : '')} failed to fully process`)
            }
            throw Error('Error processing API Post Request')
        }) as Promise<T>
}

export const ApiPut = <T extends unknown>(apiRoute: string, queryStringObject?: Record<string, unknown>, object?: Record<string, unknown>): Promise<T> => {
    const queryString = queryStringObject ? '?' + QueryString.stringify(queryStringObject) : null

    const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }

    const init = {
        headers: headers,
        method: 'PUT',
        body: JSON.stringify(object)
    }

    return fetch(configData.ApiBaseUrl + '/' + apiRoute + (queryString ? queryString : ''), init)
        .then(res => {
            if (res.status !== 200) {
                return res.text()
                    .catch(() => '')
                    .then(body => {
                        Log(LogLevel.Error, `PUT request ${configData.ApiBaseUrl + '/' + apiRoute + (queryString ? queryString : '')} failed with message ${body}`)
                        throw buildApiPutError(body)
                    })
            }
            if (res.ok) {
                return res.text().then(t => {
                    return Promise.resolve(t.length > 0 ? JSON.parse(t) as T : true)
                })
            }
            Log(LogLevel.Error, `PUT request ${configData.ApiBaseUrl + '/' + apiRoute + (queryString ? queryString : '')} failed to fully process`)
            throw Error('Error processing API Put Request')
        }) as Promise<T>
}

export const ApiPostFile = <T extends unknown>(apiRoute: string, data: BodyInit): Promise<T> => {
    const init = {
        method: 'POST',
        body: data
    }

    return fetch(configData.ApiBaseUrl + '/' + apiRoute, init)
        .then(res => {
            if (res.status !== 200) {
                res.text().then(t => {
                    Log(LogLevel.Error, `POST File request ${configData.ApiBaseUrl + '/' + apiRoute} failed with message ${t}`)
                })
                // eslint-disable-next-line no-console
                .catch(console.log)
                
                throw Error('There was an issue processing your request')
            }

            if (!res.bodyUsed) {
                if (res.ok) return true
                else return false
            }
            return res.json()
        }) as Promise<T>
}

export const ApiPatch = <T extends unknown>(apiRoute: string, queryStringObject?: Record<string, unknown>, object?: Record<string, unknown>): Promise<T> => {
    const queryString = queryStringObject ? '?' + QueryString.stringify(queryStringObject) : null

    const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }

    const init = {
        headers: headers,
        method: 'PATCH',
        body: JSON.stringify(object)
    }

    return fetch(configData.ApiBaseUrl + '/' + apiRoute + (queryString ? queryString : ''), init)
        .then(res => {
            if (res.status !== 200) {
                res.text().then(t => {
                    Log(LogLevel.Error, `PATCH request ${configData.ApiBaseUrl + '/' + apiRoute + (queryString ? queryString : '')} failed with message ${t}`)
                })
                // eslint-disable-next-line no-console
                .catch(console.log)
                throw Error('There was an issue processing your request')
            }
            if (res.ok) {
                return res.text().then(t => {
                    return Promise.resolve(t.length > 0 ? JSON.parse(t) as T : true)
                })
            }
            Log(LogLevel.Error, `PATCH request ${configData.ApiBaseUrl + '/' + apiRoute + (queryString ? queryString : '')} failed to fully process`)
            throw Error('Error processing API Patch Request')
        }) as Promise<T>
}